import type { UploadResponse } from '~/helpers/files/upload-with-progress'
import { useCallback, useEffect, useRef, useState } from 'react'
import { remainingUploadSeconds } from '~/helpers/files/upload-eta'
import { UploadAbortedError, uploadWithProgress } from '~/helpers/files/upload-with-progress'

/**
 * Où en est un envoi. Deux temps distincts : l'envoi, mesurable, puis le
 * traitement serveur, dont on ne connaît que le début et la fin.
 */
export type UploadState
  = | { phase: 'idle' }
    | { phase: 'uploading', loaded: number, total: number, remainingSeconds: number | null }
    | { phase: 'processing' }

/** Comment l'envoi s'est terminé. `response` porte aussi les refus (4xx, 5xx). */
export type UploadOutcome
  = | { kind: 'response', response: UploadResponse }
    | { kind: 'cancelled' }
    | { kind: 'networkError' }

/** L'envoi en cours, et s'il est déjà tout entier parti. */
type InFlight = { controller: AbortController, sent: boolean }

/**
 * Envoi d'un formulaire avec sa progression et son annulation (#310). Un seul
 * envoi à la fois.
 *
 * Seul l'envoi s'annule : une fois le fichier parti, le serveur le traite dans
 * la même requête et va au bout, que le navigateur attende ou non. Couper la
 * connexion à ce moment ferait croire à une annulation qui n'a pas eu lieu.
 * Quitter l'écran pendant l'envoi l'annule donc, pendant le traitement non.
 */
export function useUploadWithProgress() {
  const [state, setState] = useState<UploadState>({ phase: 'idle' })
  const inFlight = useRef<InFlight | null>(null)

  const cancel = useCallback(() => {
    if (inFlight.current && !inFlight.current.sent) inFlight.current.controller.abort()
  }, [])

  useEffect(() => cancel, [cancel])

  const upload = useCallback(async (path: string, form: FormData): Promise<UploadOutcome> => {
    inFlight.current?.controller.abort()
    const current: InFlight = { controller: new AbortController(), sent: false }
    inFlight.current = current
    const startedAt = Date.now()
    setState({ phase: 'uploading', loaded: 0, total: 0, remainingSeconds: null })

    try {
      const response = await uploadWithProgress(path, form, {
        signal: current.controller.signal,
        onProgress: (loaded, total) => setState({
          phase: 'uploading',
          loaded,
          total,
          remainingSeconds: remainingUploadSeconds(loaded, total, Date.now() - startedAt),
        }),
        onSent: () => {
          current.sent = true
          setState({ phase: 'processing' })
        },
      })
      return { kind: 'response', response }
    } catch (error) {
      return error instanceof UploadAbortedError ? { kind: 'cancelled' } : { kind: 'networkError' }
    } finally {
      if (inFlight.current === current) {
        inFlight.current = null
        setState({ phase: 'idle' })
      }
    }
  }, [])

  return { state, upload, cancel }
}
